// frontend/src/components/InstructorSection.jsx
import React, { useState, useEffect } from "react";

const InstructorSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Join us",
      description:
        "Drive With Portland is expanding and welcomes newly qualified and experienced instructors across London. We offer one-to-one training, ongoing development, and full professional support from DVSA-qualified trainers, ensuring confidence, career growth, and long-term success from day one.",
      note: "Note: Currently we are not recruiting",
      features: [
        { icon: "fa-clock", text: "Flexible Hours" },
        { icon: "fa-chart-line", text: "Control of Your Earnings" },
        { icon: "fa-user-graduate", text: "Professional Training" },
        { icon: "fa-handshake", text: "Full Support" },
      ],
    },
    {
      title: "Become Driving Instructor",
      description:
        "Do you want to become a qualified, independent DVSA-approved driving instructor? Launch your new career with us. As a leading London driving school, we offer expert training and proven experience. Complete the form below and our team will contact you.",
      note: null,
      features: [
        { icon: "fa-car", text: "Modern Vehicles" },
        { icon: "fa-book", text: "Latest Resources" },
        { icon: "fa-certificate", text: "Certification" },
        { icon: "fa-users", text: "Community Support" },
      ],
    },
  ];

  // Define nextSlide BEFORE using it in useEffect
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  // Now useEffect can safely use nextSlide
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 8000);

    return () => clearInterval(interval);
  }, []); // Empty dependency array since nextSlide is stable

  return (
    <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-blue-50 to-sky-100">
      <div className="container mx-auto max-w-6xl">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left Image Column */}
          <div className="md:col-span-4 lg:text-4xl text-red-600 relative h-80 md:h-auto lg:h-96 xl:h-[500px] bg-gradient-to-br from-sky-200 to-cyan-300 rounded-br-[180px] rounded-tl-[180px] overflow-hidden">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                  currentSlide === index ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                <img
                  src="/img/become-instractor.jpg"
                  alt={slide.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>
            ))}
          </div>

          {/* Right Content Column */}
          <div className="md:col-span-8 md:ml-8 lg:ml-12 xl:ml-16 relative min-h-[400px]">
            {/* Navigation Arrow */}
            <div className="absolute right-0 top-0 z-10">
              <button
                onClick={nextSlide}
                className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center hover:shadow-xl border border-gray-200 transition-all duration-300 ease-in-out hover:scale-110 hover:bg-sky-500 group"
                aria-label="Next slide"
              >
                <i className="fa-solid fa-arrow-right-long text-blue-600 text-lg group-hover:text-white transition-colors"></i>
              </button>
            </div>

            {/* Slides Content */}
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`transition-all duration-500 ease-in-out ${
                  currentSlide === index
                    ? "opacity-100 translate-y-0"
                    : "absolute opacity-0 translate-y-4 pointer-events-none"
                }`}
                style={{
                  top: 0,
                  left: 0,
                  right: 0,
                }}
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {slide.title}
                </h2>

                <p className="text-lg md:text-xl lg:text-2xl text-gray-700 mb-6 max-w-2xl leading-relaxed">
                  {slide.description}
                </p>

                {slide.note && (
                  <p className="text-lg md:text-xl lg:text-2xl text-red-600 font-bold  max-w-2xl  py-2 rounded-lg inline-block">
                    {slide.note}
                  </p>
                )}

                <div className="flex flex-wrap gap-4 mt-8">
                  {slide.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center bg-white rounded-xl px-5 py-3 shadow-sm border-l-4 border-sky-500 hover:-translate-y-1 hover:shadow-md transition-all duration-300 ease-in-out"
                    >
                      <div className="mr-3 text-sky-500 text-xl">
                        <i className={`fas ${feature.icon}`}></i>
                      </div>
                      <div className="font-semibold text-slate-800">
                        {feature.text}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InstructorSection;
